"use client";

import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

export default function ChangeOrdersTab({ project, setProject }) {
  const navigate = useNavigate();
  const [changeOrders, setChangeOrders] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("billable");
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchChangeOrders = async () => {
      setIsLoading(true);

      if (project.version === 2) {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/changeOrders2/project/${project._id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          const updatedOrders = response.data.changeOrders.map((co) => ({
            ...co,
            createdAt: co.createdAt || "2025-09-25T00:00:00.000Z",
          }));
          setChangeOrders(updatedOrders);
        } catch (err) {
          setError(
            "Failed to load change orders: " +
              (err.response?.data?.message || err.message),
          );
        } finally {
          setIsLoading(false);
        }
      } else {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/changeOrders/project/${project._id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          const updatedOrders = response.data.map((co) => ({
            ...co,
            createdAt: co.createdAt || "2025-09-25T00:00:00.000Z",
          }));
          setChangeOrders(updatedOrders);
        } catch (err) {
          setError(
            "Failed to load change orders: " +
              (err.response?.data?.message || err.message),
          );
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchChangeOrders();
  }, [project._id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleGenerateChangeOrder = () => {
    if (project.version === 2) {
      navigate(`/projects/${project._id}/change-orders2/new`, {
        state: {
          budgetItems: project.budgets.flatMap((budget) =>
            (budget.lineItems || []).map((item) => ({
              ...item,
              budgetId: budget._id,
              budgetTitle: budget.title,
            })),
          ),
        },
      });
    } else {
      navigate(`/projects/${project._id}/change-orders/new`, {
        state: {
          budgetItems: project.budgets.flatMap((budget) =>
            (budget.lineItems || []).map((item) => ({
              ...item,
              budgetId: budget._id,
              budgetTitle: budget.title,
            })),
          ),
        },
      });
    }
  };

  const formatNumber = (num) =>
    num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const handleSort = (field) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleStatusSelect = (status) => {
    setStatusFilter(status);
    setIsStatusDropdownOpen(false);
  };

  const handleClearFilter = () => {
    setStatusFilter("all");
    setIsStatusDropdownOpen(false);
  };

  const filteredChangeOrders = changeOrders
    .filter((co) =>
      activeTab === "billable" ? co.summary.billable : !co.summary.billable,
    )
    .filter((co) =>
      statusFilter === "all"
        ? true
        : co.approvalStatus.toLowerCase() === statusFilter.toLowerCase(),
    )
    .sort((a, b) => {
      const aValue =
        sortField === "createdAt" ? new Date(a[sortField]) : a[sortField];
      const bValue =
        sortField === "createdAt" ? new Date(b[sortField]) : b[sortField];

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

  const subtotal = filteredChangeOrders.reduce(
    (acc, co) => ({
      totalCost: acc.totalCost + co.totalCost,
      totalPrice: acc.totalPrice + co.totalPrice,
    }),
    { totalCost: 0, totalPrice: 0 },
  );

  const subtotal2 = filteredChangeOrders.reduce(
    (acc, co) => ({
      totalCost: acc.totalCost + co.totalBudgetCost,
      totalPrice: acc.totalPrice + co.totalCustomerPrice,
    }),
    { totalCost: 0, totalPrice: 0 },
  );

  const handleExportToExcel = () => {
    // Prepare data for Excel
    const data = filteredChangeOrders.map((co) => ({
      Title: co.title,
      "Change Order #": co.changeOrderNumber,
      "Total Cost": `$${formatNumber(co.totalCost)}`,
      "Total Price": `$${formatNumber(co.totalPrice)}`,
      Status:
        co.approvalStatus.charAt(0).toUpperCase() + co.approvalStatus.slice(1),
      Date: formatDate(co.createdAt),
    }));

    // Add subtotal row
    if (filteredChangeOrders.length > 0) {
      data.push({
        Title: "Subtotal",
        "Change Order #": "",
        "Total Cost": `$${formatNumber(subtotal.totalCost)}`,
        "Total Price": `$${formatNumber(subtotal.totalPrice)}`,
        Status: "",
        Date: "",
      });
    }

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    // Create workbook and add the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Change Orders`,
    );
    console.log(project);
    // Generate and download Excel file
    XLSX.writeFile(wb, `${project.projectName}_change_order_summary.xlsx`);
  };

  if (isLoading)
    return (
      <div className="text-center py-4 text-gray-200 bg-gray-700">
        Loading...
      </div>
    );
  if (error)
    return (
      <div className="text-red-400 text-center py-4 bg-gray-700">{error}</div>
    );

  return (
    <div className="bg-gray-700 p-6 rounded-lg shadow-md border border-gray-600">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">Change Orders</h3>
        <div className="flex space-x-2">
          <button
            onClick={handleExportToExcel}
            className="px-4 py-2 text-sm text-white font-semibold bg-blue-500 rounded-md hover:bg-blue-600 transition-colors duration-200"
          >
            Export to Excel
          </button>
          <button
            onClick={handleGenerateChangeOrder}
            className="px-4 py-2 text-sm text-white font-semibold bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
          >
            Generate Change Order
          </button>
        </div>
      </div>

      <div className="flex mb-4">
        <button
          onClick={() => setActiveTab("billable")}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === "billable"
              ? "bg-orange-400 text-white"
              : "bg-gray-800 text-gray-400"
          } rounded-l-md hover:bg-orange-500 transition-colors duration-200`}
        >
          Billable
        </button>
        <button
          onClick={() => setActiveTab("non-billable")}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === "non-billable"
              ? "bg-orange-400 text-white"
              : "bg-gray-800 text-gray-400"
          } rounded-r-md hover:bg-orange-500 transition-colors duration-200`}
        >
          Non-Billable
        </button>
      </div>

      <div className="overflow-hidden rounded-lg shadow-xl">
        <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
          <thead className="bg-gray-800">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tl-lg cursor-pointer"
                onClick={() => handleSort("title")}
              >
                Title{" "}
                {sortField === "title" && (
                  <span className="text-orange-400">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort("changeOrderNumber")}
              >
                Change Order #{" "}
                {sortField === "changeOrderNumber" && (
                  <span className="text-orange-400">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort("totalCost")}
              >
                Total Cost{" "}
                {sortField === "totalCost" && (
                  <span className="text-orange-400">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort("totalPrice")}
              >
                Total Price{" "}
                {sortField === "totalPrice" && (
                  <span className="text-orange-400">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider relative">
                <div
                  className="flex items-center cursor-pointer"
                  onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                >
                  Status
                  {statusFilter !== "all" && (
                    <span className="ml-2 w-2 h-2 bg-orange-400 rounded-full"></span>
                  )}
                </div>
                {isStatusDropdownOpen && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-10 mt-2 w-32 bg-gray-800 rounded-md shadow-lg"
                  >
                    <div className="py-1">
                      {["all", "Pending", "Approved", "Rejected"].map(
                        (status) => (
                          <button
                            key={status}
                            onClick={() => handleStatusSelect(status)}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-600"
                          >
                            {status === "all" ? "All" : status}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                )}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort("createdAt")}
              >
                Date{" "}
                {sortField === "createdAt" && (
                  <span className="text-orange-400">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-600">
            {filteredChangeOrders.map((co) => (
              <tr
                key={co._id}
                onClick={(e) => {
                  if (e.target.closest(".actions-column")) return;
                  if (project.version === 2) {
                    navigate(
                      `/projects/${project._id}/change-orders2/${co._id}/edit`,
                    );
                  } else {
                    navigate(
                      `/projects/${project._id}/change-orders/${co._id}/edit`,
                    );
                  }
                }}
                className="hover:bg-gray-500 cursor-pointer transition-colors duration-200"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                  {co.title}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                  {co.changeOrderNumber}
                </td>
                {project.version === 2 ? (
                  <>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                      ${formatNumber(co.totalBudgetCost)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                      ${formatNumber(co.totalCustomerPrice)}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                      ${formatNumber(co.totalCost)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                      ${formatNumber(co.totalPrice)}
                    </td>
                  </>
                )}

                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      co.approvalStatus.toLowerCase() === "pending"
                        ? "bg-yellow-600/20 text-yellow-400"
                        : co.approvalStatus.toLowerCase() === "approved"
                          ? "bg-green-600/20 text-green-400"
                          : "bg-red-600/20 text-red-400"
                    }`}
                  >
                    {co.approvalStatus.charAt(0).toUpperCase() +
                      co.approvalStatus.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                  {formatDate(co.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
          {filteredChangeOrders.length > 0 && (
            <tfoot className="bg-gray-800">
              <tr>
                <td
                  colSpan="2"
                  className="px-6 py-3 text-left text-sm text-gray-200 font-semibold"
                >
                  Subtotals
                </td>

                {project.version === 2 ? (
                  <>
                    <td className="px-6 py-3 text-left text-sm text-gray-200 font-semibold">
                      ${formatNumber(subtotal2.totalCost)}
                    </td>
                    <td className="px-6 py-3 text-left text-sm text-gray-200 font-semibold">
                      ${formatNumber(subtotal2.totalPrice)}
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-6 py-3 text-left text-sm text-gray-200 font-semibold">
                      ${formatNumber(subtotal.totalCost)}
                    </td>
                    <td className="px-6 py-3 text-left text-sm text-gray-200 font-semibold">
                      ${formatNumber(subtotal.totalPrice)}
                    </td>
                  </>
                )}

                <td colSpan="3" className="px-6 py-3"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {filteredChangeOrders.length === 0 || statusFilter !== "all" ? (
        <div className="mt-4 flex justify-between items-center">
          {filteredChangeOrders.length === 0 && (
            <p className="text-gray-400">
              No {activeTab === "billable" ? "billable" : "non-billable"} change
              orders for selected status.
            </p>
          )}
          {statusFilter !== "all" && (
            <button
              onClick={handleClearFilter}
              className="ml-auto px-4 py-2 text-sm text-white font-semibold bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
            >
              Clear Filter
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
